import { Customer, CustomerOverride } from '../types';
import type { DocumentCustomerState, DocumentKind } from './customerQuickEdit';

/**
 * Abweichende Kundenangaben je Beleg — die Regeln dazu.
 *
 * Der Bearbeiten-Dialog im Beleg schrieb bisher ausnahmslos in den
 * Kundenstamm. Das ist der Normalfall (ein Umzug betrifft alle kuenftigen
 * Belege), aber nicht der einzige: verlangt EIN Auftraggeber fuer EIN Projekt
 * eine andere Rechnungsanschrift — eigene Vergabestelle, abweichende
 * Konzerngesellschaft, andere Rechnungs-E-Mail —, dann darf diese Angabe
 * gerade NICHT auf die uebrigen Belege desselben Kunden durchschlagen.
 *
 * Deshalb hier zwei Reichweiten statt einer. Die Logik liegt im Service und
 * nicht in der Maske, weil das Projekt keine DOM-Testumgebung hat: pruefbar
 * sein muss vor allem, WAS bei welcher Wahl geschrieben wird und was der
 * Dialog dem Nutzer darueber zusagt.
 */

/**
 * Reichweite einer Aenderung aus dem Beleg heraus.
 *
 * `global`   — Kundenstamm. Wirkt auf jeden kuenftigen Beleg dieses Kunden.
 * `document` — nur dieser Beleg (details.customerOverride). Der Kundenstamm
 *              bleibt unberuehrt.
 */
export type CustomerEditScope = 'global' | 'document';

/**
 * Die Felder, die ein Beleg abweichend fuehren darf.
 *
 * Deckungsgleich mit dem, was die Schnellbearbeitung anbietet, und das ist
 * kein Zufall: beides folgt dem Andruck (generatorInvoice/generatorQuote
 * drucken name/name2/name3, street/zip/city bzw. ersatzweise address) plus
 * E-Mail und Telefon fuer den Versand.
 *
 * `customerNumber` fehlt bewusst — sie ist die Debitorennummer und damit das
 * DATEV-Konto; ein Beleg, der den Kunden unter einer anderen Nummer fuehrt,
 * verbuchte sich auf einen fremden Debitor. Ebenso fehlen die Konditionen
 * (Zahlungsziel, Skonto, Preise): das sind Eigenschaften des Kunden, keine
 * Andruckangaben, und sie stehen dem Beleg bereits als eigene Felder offen.
 */
export const OVERRIDABLE_CUSTOMER_KEYS = [
    'customerType',
    'salutation',
    'title',
    'firstName',
    'lastName',
    'name',
    'name2',
    'name3',
    'address',
    'street',
    'zip',
    'city',
    'email',
    'phone',
] as const;

type OverridableKey = (typeof OVERRIDABLE_CUSTOMER_KEYS)[number];

const OVERRIDABLE = new Set<string>(OVERRIDABLE_CUSTOMER_KEYS);

/**
 * Aus dem Aenderungssatz der Schnellbearbeitung die belegbezogene Abweichung
 * bilden.
 *
 * Eingang ist `CustomerUpdatePlan.updates` — also genau die Felder, die
 * gegenueber dem geladenen Kundenstamm anders lauten, samt bereits
 * nachgezogener Alt-Anschrift (planCustomerUpdate). Damit gilt hier dieselbe
 * Diff-Regel wie beim globalen Speichern; eine zweite, danebenstehende
 * Vergleichsformel koennte ihr widersprechen.
 *
 * Herausgefiltert wird alles, was der Beleg nicht abweichend fuehren darf —
 * `changeLog` etwa steckt im selben Aenderungssatz, gehoert aber ausschliesslich
 * in den Stamm.
 *
 * `undefined` heisst: keine Abweichung. Ein LEERER Wert dagegen ist eine
 * Abweichung ("auf diesem Beleg steht in Zeile 3 nichts") und bleibt erhalten.
 */
export const customerOverrideFromUpdates = (
    updates: Partial<Customer>,
): CustomerOverride | undefined => {
    const out: Record<string, unknown> = {};
    (Object.keys(updates) as (keyof Customer)[]).forEach((k) => {
        if (!OVERRIDABLE.has(k as string)) return;
        const v = updates[k];
        // Ein fehlender Wert ist keine Aussage: er wuerde beim Anwenden nichts
        // tun und traegt in der Ablage nur Rauschen ein.
        if (v === undefined) return;
        out[k as string] = v;
    });
    return Object.keys(out).length ? (out as CustomerOverride) : undefined;
};

/** Traegt dieser Beleg ueberhaupt eine Abweichung? */
export const hasCustomerOverride = (o?: CustomerOverride | null): boolean =>
    !!o && Object.keys(o).some((k) => OVERRIDABLE.has(k) && (o as Record<string, unknown>)[k] !== undefined);

/**
 * Den Kundenstamm mit der Abweichung des Belegs ueberlagern.
 *
 * Das Ergebnis ist der Kunde, wie ihn DIESER Beleg ausgibt — Vorschau, PDF,
 * Kopfzeile, E-Rechnung und der beim Ausstellen eingefrorene Snapshot lesen
 * alle hier.
 *
 * Ueberlagert wird feldweise und nur, was in der Abweichung steht: aendert
 * sich im Stamm die Strasse, druckt ein Beleg, der bloss "z. Hd. Frau Muster"
 * abweichend fuehrt, weiterhin die neue Strasse. Genau dafuer ist die
 * Abweichung ein Diff und kein zweiter Kundensatz.
 *
 * `id` und `customerNumber` bleiben in jedem Fall die des Stamms — sie stehen
 * nicht in OVERRIDABLE_CUSTOMER_KEYS und koennen deshalb gar nicht abweichen.
 */
export const applyCustomerOverride = <T extends Customer>(
    customer: T,
    override?: CustomerOverride | null,
): T => {
    if (!hasCustomerOverride(override)) return customer;
    const out = { ...customer } as Record<string, unknown>;
    (Object.keys(override as CustomerOverride) as string[]).forEach((k) => {
        if (!OVERRIDABLE.has(k)) return;
        const v = (override as Record<string, unknown>)[k];
        if (v === undefined) return;
        out[k] = v;
    });
    return out as T;
};

/**
 * Dasselbe fuer einen Kunden, der noch gar nicht geladen ist.
 *
 * Die Angebotsliste baut fuer einen unbekannten Kunden einen Platzhalter, und
 * die Rechnungsmaske kennt kurz nach dem Oeffnen noch gar keinen. Eine
 * Abweichung darf deshalb nicht daran haengen, dass der Stamm schon da ist.
 */
export const applyCustomerOverrideOptional = <T extends Customer>(
    customer: T | undefined,
    override?: CustomerOverride | null,
): T | undefined => (customer ? applyCustomerOverride(customer, override) : customer);

/**
 * Sind zwei Abweichungen inhaltlich dieselbe?
 *
 * Feldweise und nicht ueber JSON.stringify: dieselbe Abweichung kommt aus dem
 * jsonb in beliebiger Schluesselreihenfolge zurueck, und ein Textvergleich
 * haette den Dialog schon beim blossen Oeffnen als geaendert gemeldet.
 */
export const sameCustomerOverride = (
    a?: CustomerOverride | null,
    b?: CustomerOverride | null,
): boolean =>
    OVERRIDABLE_CUSTOMER_KEYS.every(
        (k) =>
            ((a ?? {}) as Record<string, unknown>)[k] === ((b ?? {}) as Record<string, unknown>)[k],
    );

/** Fachliche Gruppen der Abweichung — fuer den Hinweis am Beleg. */
const OVERRIDE_GROUPS: { label: string; keys: OverridableKey[] }[] = [
    {
        label: 'Name',
        keys: ['customerType', 'salutation', 'title', 'firstName', 'lastName', 'name', 'name2', 'name3'],
    },
    { label: 'Anschrift', keys: ['address', 'street', 'zip', 'city'] },
    { label: 'E-Mail', keys: ['email'] },
    { label: 'Telefon', keys: ['phone'] },
];

/**
 * Woran der Beleg abweicht, in Worten ("Name", "Anschrift", "E-Mail").
 *
 * Gruppiert und nicht feldweise: "name2, name3, zip, city" sagt dem
 * Bearbeiter weniger als "Name, Anschrift", und die Zeile im Kopf hat Platz
 * fuer eine Aufzaehlung, nicht fuer eine Feldliste.
 */
export const customerOverrideFields = (o?: CustomerOverride | null): string[] => {
    if (!hasCustomerOverride(o)) return [];
    const rec = o as Record<string, unknown>;
    return OVERRIDE_GROUPS.filter((g) => g.keys.some((k) => rec[k] !== undefined)).map((g) => g.label);
};

/** Dieselbe Auskunft als Satzteil — leer, wenn es nichts zu sagen gibt. */
export const customerOverrideSummary = (o?: CustomerOverride | null): string =>
    customerOverrideFields(o).join(', ');

/**
 * Darf dieser Beleg ueberhaupt eine eigene Abweichung fuehren?
 *
 * Nur solange er offen ist. Ein festgeschriebener Beleg liest seinen Kunden
 * aus dem `customerSnapshot`, und `details` sperrt dort ohnehin der
 * Festschreibungs-Trigger — eine belegbezogene Aenderung waere entweder
 * wirkungslos oder ein Bruch der Festschreibung. Beides darf der Dialog nicht
 * anbieten, sonst sagt er etwas zu, was er nicht halten kann.
 */
export const canScopeToDocument = (state: DocumentCustomerState): boolean => state === 'open';

/**
 * Ueberlagert die belegbezogene Abweichung diesen Beleg ueberhaupt noch?
 *
 * Eigene Frage neben canScopeToDocument, auch wenn die Antwort dieselbe ist:
 * "darf ich sie hier aendern" und "gilt sie hier" sind zwei Aussagen, und dass
 * sie zusammenfallen, ist eine Entscheidung und kein Zufall.
 *
 * `frozen` — nein: der Beleg liest seinen Kunden aus dem customerSnapshot, und
 * dort steckt die Abweichung laengst eingerechnet drin.
 *
 * `unfrozen` — ebenfalls nein, und DAS ist die Aenderung. Bisher ueberlagerte
 * sie hier weiter (die Masken fragten `!frozen`), waehrend der
 * Bearbeiten-Dialog sie ausblendete und das Aufraeumen nach dem Speichern
 * ausliess. Der Dialog sagte damit "die Aenderung schlaegt auch hier durch —
 * der Beleg bleibt NICHT unveraendert" (customerQuickEdit: customerEditNotice)
 * und Vorschau wie PDF druckten trotzdem die alte, abweichende Anschrift; ueber
 * die Oberflaeche liess sie sich nicht mehr entfernen, denn ein geschlossener
 * Beleg speichert seine `details` nicht mehr (InvoiceEditor: patch nur wenn
 * editable; QuoteEditor: Autosave nur wenn nicht locked). Von den beiden
 * moeglichen Aufloesungen bleibt deshalb nur diese: was sich nicht mehr
 * bearbeiten laesst, darf auch nicht mehr ueberlagern. Der Beleg folgt dann dem
 * lebenden Kundenstamm — genau das, was der Dialog zusagt.
 *
 * Die Abweichung bleibt dabei in `details` stehen und wird nicht geloescht: wird
 * der Beleg wieder zur Bearbeitung geoeffnet, gilt sie erneut. Ein
 * festgeschriebener Beleg wird von alldem nicht beruehrt.
 */
export const customerOverrideApplies = (state: DocumentCustomerState): boolean =>
    state === 'open';

/** Warum die belegbezogene Wahl gesperrt ist — null, wenn sie offensteht. */
export const documentScopeBlockedReason = (
    kind: DocumentKind,
    state: DocumentCustomerState,
): string | null => {
    if (canScopeToDocument(state)) return null;
    return kind === 'invoice'
        ? 'Nur für Rechnungsentwürfe: ein ausgestellter Beleg ist festgeschrieben und trägt die Anschrift, mit der er ausgestellt wurde.'
        : 'Nur für Angebotsentwürfe: ein versendetes oder abgeschlossenes Angebot behält die Anschrift, mit der es verschickt wurde.';
};

/**
 * Welche Reichweite der Dialog beim Oeffnen vorschlaegt.
 *
 * Der bestehende Zustand des Belegs, nicht die haeufigere Wahl: fuehrt der
 * Beleg bereits eine Abweichung, ist "nur dieser Beleg" das, was der
 * Bearbeiter gerade vor sich sieht — mit `global` vorbelegt wuerde ein
 * unachtsames Uebernehmen die Abweichung in den Stamm heben. Ohne Abweichung
 * bleibt es beim Kundenstamm, also beim bisherigen Verhalten.
 */
export const defaultCustomerEditScope = (
    state: DocumentCustomerState,
    override?: CustomerOverride | null,
): CustomerEditScope =>
    canScopeToDocument(state) && hasCustomerOverride(override) ? 'document' : 'global';

/** Beschriftung der belegbezogenen Wahl. */
export const documentScopeLabel = (kind: DocumentKind): string =>
    kind === 'invoice' ? 'Nur für diese Rechnung' : 'Nur für dieses Angebot';

/**
 * Die Zusage des Dialogs bei belegbezogener Wahl.
 *
 * Sagt beides: was NICHT passiert (Kundenstamm bleibt) und was mit den
 * kuenftigen Belegen desselben Projekts geschieht — die uebernehmen die
 * Abweichung, und wer das nicht erwartet, haelt es spaeter fuer einen Fehler.
 */
export const documentScopeNotice = (kind: DocumentKind, projectSticky: boolean): string => {
    const base =
        kind === 'invoice'
            ? 'Die Angaben gelten NUR für diese Rechnung. Der Kundenstamm und alle anderen Belege dieses Kunden bleiben unverändert.'
            : 'Die Angaben gelten NUR für dieses Angebot. Der Kundenstamm und alle anderen Belege dieses Kunden bleiben unverändert.';
    return projectSticky
        ? `${base} Der nächste Entwurf zu diesem Projekt übernimmt die Abweichung als Vorbelegung.`
        : base;
};

/**
 * Der Zusatzsatz, wenn global gespeichert wird, OBWOHL der Beleg eine
 * Abweichung fuehrt.
 *
 * Der Dialog zeigt in diesem Fall die abweichenden Werte an — sie sind das,
 * was der Beleg druckt. Wer dann auf "Kundenstamm" umstellt, hebt genau diese
 * Werte in den Stamm; die belegbezogene Abweichung entfaellt danach, weil sie
 * nichts mehr abweichen wuerde. Das gehoert vor den Klick, nicht danach.
 */
export const liftOverrideWarning = (kind: DocumentKind): string =>
    kind === 'invoice'
        ? 'Achtung: Dieser Beleg führt abweichende Angaben. Beim Speichern in den Kundenstamm werden sie zu Stammdaten und gelten ab sofort für ALLE Belege dieses Kunden; die belegbezogene Abweichung entfällt.'
        : 'Achtung: Dieses Angebot führt abweichende Angaben. Beim Speichern in den Kundenstamm werden sie zu Stammdaten und gelten ab sofort für ALLE Belege dieses Kunden; die belegbezogene Abweichung entfällt.';
